import React from 'react';

import { css } from '@emotion/react';
import { CssVars, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import { AddressParts } from '@x1/util-format';
import { ImageProps } from 'next/image';

import { AddressType } from 'src/clients/Network/gql/types/address';
import GoogleMapsClient from 'src/clients/external/GoogleMapsClient';

interface PropsIface {
  address: AddressType;
  width: number;
  height: number;
  onChangeAddress?: () => void;
}

const MapThumbnail = ({
  address,
  width,
  height,
  onChangeAddress,
}: PropsIface & Omit<ImageProps, 'width' | 'height' | 'source' | 'src'>) => {
  const addressLine1 = AddressParts(address).addressLine1;
  const addressLine2 = AddressParts(address).addressLine2;
  const addressString = addressLine1 + ' ' + addressLine2;

  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [overlayHeight, setOverlayHeight] = React.useState(0);

  // Measure overlay height after render
  React.useLayoutEffect(() => {
    if (overlayRef.current) {
      setOverlayHeight(overlayRef.current.offsetHeight);
    }
  }, [onChangeAddress, addressLine1, addressLine2]); // Re-measure when content changes

  const source = React.useMemo(() => {
    const flooredWidth = Math.floor(width);
    const flooredMapHeight = height;
    return {
      uri: GoogleMapsClient.getStaticMapUrl({
        address: addressString,
        width: flooredWidth,
        height: flooredMapHeight,
        markerHexCode: CssVars.semantic.positive.base.replace('#', '0x'),
      }),
      width: flooredWidth,
      height: flooredMapHeight,
    };
  }, [address, width, height]);

  const containerStyle = React.useMemo(() => {
    // Shift map up by half the overlay height to center it in visible area
    const verticalOffset = overlayHeight > 0 ? -(overlayHeight / 2) : 0;

    return {
      aspectRatio: `${width} / ${height}`,
      backgroundImage: `url(${source.uri})`,
      backgroundSize: 'cover',
      backgroundPosition: `center calc(50% + ${verticalOffset}px)`,
      backgroundRepeat: 'no-repeat',
    };
  }, [height, width, source.uri, overlayHeight]);

  const handleChangeAddress = React.useCallback(() => {
    onChangeAddress?.();
  }, []);

  return (
    <div css={root} style={containerStyle}>
      <div ref={overlayRef} css={overlayStyle}>
        <div css={addressContainer}>
          <Type.Text size='M' isBold>
            {addressLine1}
          </Type.Text>
          {addressLine2 && (
            <Type.Text size='S' color={NeutralColorToken.Fg2}>
              {addressLine2}
            </Type.Text>
          )}
        </div>
        {onChangeAddress && (
          <div css={footer}>
            <TextButton
              type='anchor'
              label='Ship to a different address'
              onClick={handleChangeAddress}
              variant='standalone'
              color={NeutralColorToken.Fg1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const root = css`
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  border: 1px solid ${CssVars.neutral.bg3};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const overlayStyle = css`
  background: ${CssVars.neutral.bg2};
  padding: 24px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const addressContainer = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const footer = css`
  display: flex;
  padding-top: 16px;
  border-top: 1px solid ${CssVars.neutral.bg3};
`;

export default MapThumbnail;
